import { useEffect, useRef, useState } from 'react';
import { FLOATING_KEYBOARD_ANIM_MS } from '../utils/learnerSlideModalMotion';
import { prefersReducedMotion } from '../utils/subscriptionAccessibility';

export interface UseLearnerSlidePhaseResult<T extends string> {
  displayedPhase: T;
  /** Drives panel is-visible — false while closing, true while open (including after phase swap). */
  panelOpen: boolean;
}

/**
 * Phase hand-off: close the current panel (slide down), swap content, then open
 * the new panel (slide up) — same motion as closing/opening the quiz modal.
 */
export function useLearnerSlidePhase<T extends string>(targetPhase: T): UseLearnerSlidePhaseResult<T> {
  const [displayedPhase, setDisplayedPhase] = useState(targetPhase);
  const [panelOpen, setPanelOpen] = useState(true);
  const transitionRef = useRef(0);
  const displayedPhaseRef = useRef(displayedPhase);
  displayedPhaseRef.current = displayedPhase;

  useEffect(() => {
    if (targetPhase === displayedPhaseRef.current) return undefined;

    const transitionId = ++transitionRef.current;

    if (prefersReducedMotion()) {
      setDisplayedPhase(targetPhase);
      displayedPhaseRef.current = targetPhase;
      setPanelOpen(true);
      return undefined;
    }

    setPanelOpen(false);

    const openTimer = window.setTimeout(() => {
      if (transitionRef.current !== transitionId) return;
      setDisplayedPhase(targetPhase);
      displayedPhaseRef.current = targetPhase;
      setPanelOpen(true);
    }, FLOATING_KEYBOARD_ANIM_MS + 50);

    return () => {
      window.clearTimeout(openTimer);
    };
  }, [targetPhase]);

  return {
    displayedPhase,
    panelOpen,
  };
}
